// Comprehensive category icon resolver — matches by parent slug AND by keywords in sub-category slug/name.
// Each sub-category gets its own distinct icon based on what it represents.
import React from 'react';
import {
  // General
  CodeOutlined, BgColorsOutlined, EditOutlined, RiseOutlined,
  VideoCameraOutlined, BankOutlined, ReadOutlined, ToolOutlined,
  RobotOutlined, CustomerServiceOutlined, AppstoreOutlined,
  GlobalOutlined, BulbOutlined,
  // Programming
  Html5Outlined, AndroidOutlined, AppleOutlined, ApiOutlined, DatabaseOutlined,
  CloudServerOutlined, BugOutlined, LinkOutlined, FundProjectionScreenOutlined,
  // Design
  HighlightOutlined, FormatPainterOutlined, LayoutOutlined, PictureOutlined,
  TrademarkOutlined, GiftOutlined, AppstoreAddOutlined, MobileOutlined,
  IdcardOutlined, FileImageOutlined, BookOutlined, PrinterOutlined,
  BorderOuterOutlined, FontSizeOutlined,
  // Writing
  FileTextOutlined, ProfileOutlined, TranslationOutlined, AlignLeftOutlined,
  AuditOutlined, SnippetsOutlined, SoundOutlined,
  // Marketing
  LineChartOutlined, ShareAltOutlined, NotificationOutlined, MailOutlined,
  ShoppingCartOutlined, SearchOutlined, LikeOutlined, EnvironmentOutlined,
  // Video / Audio
  PlayCircleOutlined, CameraOutlined, AudioOutlined, FundOutlined,
  YoutubeOutlined,
  // Business
  TeamOutlined, SolutionOutlined, ContainerOutlined, CalculatorOutlined,
  DollarOutlined, StockOutlined, ScheduleOutlined, FileDoneOutlined,
  // Engineering / Architecture
  HomeOutlined, BuildOutlined, ExperimentOutlined, ThunderboltOutlined,
  ApartmentOutlined, BoxPlotOutlined,
  // Training / Education
  CompassOutlined, FormOutlined, QuestionCircleOutlined, BookFilled,
  // Support
  PhoneOutlined, MessageOutlined, TableOutlined,
} from '@ant-design/icons';

// === Parent slug → icon + color theme ===
const PARENT_META = {
  'programming-development': { icon: CodeOutlined, color: '#3498DB', accent: '#1B4F72' },
  'development-programming': { icon: CodeOutlined, color: '#3498DB', accent: '#1B4F72' },
  'programming':             { icon: CodeOutlined, color: '#3498DB', accent: '#1B4F72' },
  'development':             { icon: CodeOutlined, color: '#3498DB', accent: '#1B4F72' },
  'web-development':         { icon: CodeOutlined, color: '#3498DB', accent: '#1B4F72' },

  'design':                  { icon: BgColorsOutlined, color: '#E67E22', accent: '#D35400' },
  'graphic-design':          { icon: BgColorsOutlined, color: '#E67E22', accent: '#D35400' },
  'design-video-audio':      { icon: BgColorsOutlined, color: '#E67E22', accent: '#D35400' },

  'writing-translation':     { icon: EditOutlined, color: '#16A085', accent: '#0E7A60' },
  'writing':                 { icon: EditOutlined, color: '#16A085', accent: '#0E7A60' },
  'translation':             { icon: TranslationOutlined, color: '#16A085', accent: '#0E7A60' },

  'digital-marketing':       { icon: RiseOutlined, color: '#9B59B6', accent: '#7D3C98' },
  'marketing':               { icon: RiseOutlined, color: '#9B59B6', accent: '#7D3C98' },
  'marketing-sales':         { icon: RiseOutlined, color: '#9B59B6', accent: '#7D3C98' },

  'video-audio':             { icon: VideoCameraOutlined, color: '#E74C3C', accent: '#C0392B' },
  'video-animation-audio':   { icon: VideoCameraOutlined, color: '#E74C3C', accent: '#C0392B' },
  'video':                   { icon: VideoCameraOutlined, color: '#E74C3C', accent: '#C0392B' },
  'audio':                   { icon: AudioOutlined, color: '#E74C3C', accent: '#C0392B' },

  'business-admin':          { icon: BankOutlined, color: '#1ABC9C', accent: '#138D75' },
  'business-consulting':     { icon: BankOutlined, color: '#1ABC9C', accent: '#138D75' },
  'business':                { icon: BankOutlined, color: '#1ABC9C', accent: '#138D75' },
  'admin':                   { icon: BankOutlined, color: '#1ABC9C', accent: '#138D75' },

  'training-education':      { icon: ReadOutlined, color: '#2980B9', accent: '#1F618D' },
  'education-training':      { icon: ReadOutlined, color: '#2980B9', accent: '#1F618D' },
  'training':                { icon: ReadOutlined, color: '#2980B9', accent: '#1F618D' },
  'education':               { icon: ReadOutlined, color: '#2980B9', accent: '#1F618D' },

  'engineering-architecture': { icon: ToolOutlined, color: '#A0522D', accent: '#8B4513' },
  'engineering':              { icon: ToolOutlined, color: '#A0522D', accent: '#8B4513' },
  'architecture':             { icon: HomeOutlined, color: '#A0522D', accent: '#8B4513' },

  'ai-machine-learning':     { icon: RobotOutlined, color: '#5B5EA6', accent: '#4A4D8C' },
  'ai':                      { icon: RobotOutlined, color: '#5B5EA6', accent: '#4A4D8C' },
  'machine-learning':        { icon: RobotOutlined, color: '#5B5EA6', accent: '#4A4D8C' },
  'artificial-intelligence': { icon: RobotOutlined, color: '#5B5EA6', accent: '#4A4D8C' },

  'support-data-entry':      { icon: CustomerServiceOutlined, color: '#F39C12', accent: '#D68910' },
  'support':                 { icon: CustomerServiceOutlined, color: '#F39C12', accent: '#D68910' },
  'data-entry':              { icon: TableOutlined, color: '#F39C12', accent: '#D68910' },
};

// === Keyword-based sub-category icon matching ===
// Each entry: { match: [keywords], icon: IconComponent }
// First match wins. Keywords matched case-insensitively against slug AND Arabic/English name.
const SUB_KEYWORDS = [
  // === Design specifics ===
  { match: ['logo', 'شعار'],                            icon: TrademarkOutlined },
  { match: ['ui-ux', 'ui/ux', 'ui_ux', 'ui ux', 'تصميم ui'], icon: LayoutOutlined },
  { match: ['photoshop', 'فوتوشوب'],                   icon: PictureOutlined },
  { match: ['illustrator'],                             icon: HighlightOutlined },
  { match: ['figma'],                                   icon: AppstoreAddOutlined },
  { match: ['indesign'],                                icon: FileImageOutlined },
  { match: ['infographic', 'إنفوجرافيك', 'انفوجرافيك'], icon: FundOutlined },
  { match: ['social', 'سوشيال', 'سوشال', 'ميديا'],      icon: ShareAltOutlined },
  { match: ['brand', 'identity', 'هوية', 'علامة'],      icon: TrademarkOutlined },
  { match: ['mobile-app', 'mobile app', 'app-design', 'تطبيق جوال', 'موبايل'], icon: MobileOutlined },
  { match: ['cv', 'resume', 'سيرة ذاتية', 'سيرة'],      icon: IdcardOutlined },
  { match: ['catalog', 'كتالوج', 'كتالوغ'],             icon: BookOutlined },
  { match: ['brochure', 'بروشور', 'فلاير', 'flyer'],    icon: ProfileOutlined },
  { match: ['package', 'packaging', 'تغليف', 'عبوة'],   icon: GiftOutlined },
  { match: ['banner', 'بنر', 'لافتة'],                  icon: BorderOuterOutlined },
  { match: ['presentation', 'عرض تقديمي', 'باوربوينت', 'powerpoint'], icon: FundProjectionScreenOutlined },
  { match: ['print', 'طباعة'],                          icon: PrinterOutlined },
  { match: ['font', 'typography', 'خط', 'خطوط'],        icon: FontSizeOutlined },
  { match: ['concept', 'فكرة', 'إبداعي'],               icon: BulbOutlined },
  { match: ['motion', 'موشن', 'تحريك'],                 icon: PlayCircleOutlined },
  { match: ['consultation', 'استشار'],                  icon: MessageOutlined },
  { match: ['ad', 'إعلان', 'advertis'],                 icon: NotificationOutlined },
  { match: ['graphic', 'جرافيك'],                       icon: FormatPainterOutlined },

  // === Programming specifics ===
  { match: ['frontend', 'front-end', 'واجهة أمامية', 'html', 'css'], icon: Html5Outlined },
  { match: ['react', 'vue', 'angular', 'next'],         icon: AppstoreOutlined },
  { match: ['backend', 'back-end', 'خادم', 'سيرفر'],    icon: CloudServerOutlined },
  { match: ['android', 'أندرويد'],                      icon: AndroidOutlined },
  { match: ['ios', 'iphone', 'آيفون'],                  icon: AppleOutlined },
  { match: ['api'],                                      icon: ApiOutlined },
  { match: ['database', 'sql', 'قاعدة بيانات', 'قواعد البيانات'], icon: DatabaseOutlined },
  { match: ['wordpress', 'ووردبريس'],                   icon: GlobalOutlined },
  { match: ['ecommerce', 'متجر', 'shopify', 'woocommerce'], icon: ShoppingCartOutlined },
  { match: ['bug', 'fix', 'إصلاح', 'تصحيح', 'مشكلة'],   icon: BugOutlined },
  { match: ['integration', 'تكامل', 'ربط'],             icon: LinkOutlined },
  { match: ['python', 'java', 'php', 'node'],           icon: CodeOutlined },

  // === Writing specifics ===
  { match: ['content', 'محتوى'],                        icon: SnippetsOutlined },
  { match: ['article', 'blog', 'مقال', 'مدونة'],        icon: FileTextOutlined },
  { match: ['translation', 'ترجمة', 'translate'],       icon: TranslationOutlined },
  { match: ['proofreading', 'edit', 'تدقيق', 'مراجعة'], icon: AuditOutlined },
  { match: ['voiceover', 'voice', 'تعليق صوتي', 'صوت'], icon: SoundOutlined },
  { match: ['copywriting', 'إعلاني', 'دعائي'],          icon: AlignLeftOutlined },

  // === Marketing specifics ===
  { match: ['seo', 'سيو', 'محركات البحث'],              icon: SearchOutlined },
  { match: ['email', 'بريد', 'إيميل'],                  icon: MailOutlined },
  { match: ['analytics', 'تحليلات', 'إحصائيات'],        icon: LineChartOutlined },
  { match: ['ppc', 'ads', 'إعلان', 'حملة'],             icon: NotificationOutlined },
  { match: ['influencer', 'مؤثر'],                      icon: LikeOutlined },
  { match: ['local', 'محلي', 'gmb', 'خرائط'],           icon: EnvironmentOutlined },

  // === Video specifics ===
  { match: ['edit', 'مونتاج', 'مونتاژ', 'تحرير'],       icon: VideoCameraOutlined },
  { match: ['animation', 'رسوم متحركة', 'حركة'],        icon: PlayCircleOutlined },
  { match: ['youtube', 'يوتيوب'],                       icon: YoutubeOutlined },
  { match: ['photo', 'تصوير', 'photography'],           icon: CameraOutlined },

  // === Business specifics ===
  { match: ['accounting', 'محاسبة', 'محاسب'],           icon: CalculatorOutlined },
  { match: ['legal', 'قانون', 'محام'],                  icon: AuditOutlined },
  { match: ['financial', 'مالي', 'مالية'],              icon: DollarOutlined },
  { match: ['hr', 'موارد بشرية', 'توظيف'],              icon: TeamOutlined },
  { match: ['consulting', 'استشار', 'استشارة'],         icon: SolutionOutlined },
  { match: ['project-management', 'إدارة المشاريع'],    icon: ScheduleOutlined },
  { match: ['data', 'بيانات', 'إدخال البيانات'],        icon: TableOutlined },

  // === Education specifics ===
  { match: ['math', 'رياضيات'],                         icon: CalculatorOutlined },
  { match: ['english', 'إنجليزي', 'انجليزي'],           icon: BookFilled },
  { match: ['language', 'لغة', 'لغات'],                 icon: TranslationOutlined },
  { match: ['exam', 'اختبار', 'امتحان'],                icon: FormOutlined },
  { match: ['homework', 'واجب', 'تدريب'],               icon: BookOutlined },

  // === Engineering specifics ===
  { match: ['autocad', 'رسم هندسي'],                    icon: BuildOutlined },
  { match: ['electrical', 'كهرباء', 'كهرب'],            icon: ThunderboltOutlined },
  { match: ['mechanical', 'ميكانيك'],                   icon: BuildOutlined },
  { match: ['civil', 'مدني', 'إنشائي'],                 icon: ApartmentOutlined },
  { match: ['interior', 'تصميم داخلي', 'ديكور'],        icon: HomeOutlined },
  { match: ['3d', 'ثلاثي الأبعاد', '3d-modeling'],      icon: BoxPlotOutlined },
  { match: ['chemistry', 'كيمياء', 'lab'],              icon: ExperimentOutlined },

  // === AI specifics ===
  { match: ['chatbot', 'بوت', 'محادثة'],                icon: RobotOutlined },
  { match: ['nlp', 'معالجة اللغة'],                     icon: TranslationOutlined },
  { match: ['ml', 'machine-learning'],                  icon: RobotOutlined },

  // === Support specifics ===
  { match: ['phone', 'هاتف', 'اتصال'],                  icon: PhoneOutlined },
  { match: ['chat', 'دردشة', 'محادثة'],                 icon: MessageOutlined },
  { match: ['virtual-assistant', 'مساعد'],              icon: TeamOutlined },
  { match: ['data-entry', 'إدخال البيانات', 'إدخال بيانات'], icon: TableOutlined },
];


function matchSubIcon(slug, name) {
  const haystack = `${slug || ''} ${name || ''}`.toLowerCase();
  for (const entry of SUB_KEYWORDS) {
    if (entry.match.some(k => haystack.includes(k.toLowerCase()))) {
      return entry.icon;
    }
  }
  return null;
}


export function getCategoryIcon(slugOrCat, fallback = AppstoreOutlined) {
  let slug = '', parentSlug = '', name = '';
  if (typeof slugOrCat === 'string') {
    slug = slugOrCat.toLowerCase();
  } else if (slugOrCat && typeof slugOrCat === 'object') {
    slug = (slugOrCat.slug || '').toLowerCase();
    parentSlug = (slugOrCat.parent_slug ||
      (slugOrCat.parent && slugOrCat.parent.slug) || '').toLowerCase();
    name = `${slugOrCat.name_ar || ''} ${slugOrCat.name_en || ''}`;
  }

  // 1. Direct parent slug match (when rendering a parent itself)
  if (PARENT_META[slug]) return PARENT_META[slug].icon;

  // 2. Keyword-based sub-category match
  const subIcon = matchSubIcon(slug, name);
  if (subIcon) return subIcon;

  // 3. Fall back to parent's icon
  if (parentSlug && PARENT_META[parentSlug]) return PARENT_META[parentSlug].icon;

  return fallback;
}


/** Returns the color theme (color + accent) for a category. */
export function getCategoryColor(slugOrCat) {
  let slug = '', parentSlug = '';
  if (typeof slugOrCat === 'string') {
    slug = slugOrCat.toLowerCase();
  } else if (slugOrCat && typeof slugOrCat === 'object') {
    slug = (slugOrCat.slug || '').toLowerCase();
    parentSlug = (slugOrCat.parent_slug ||
      (slugOrCat.parent && slugOrCat.parent.slug) || '').toLowerCase();
  }
  if (PARENT_META[slug]) return { color: PARENT_META[slug].color, accent: PARENT_META[slug].accent };
  if (parentSlug && PARENT_META[parentSlug]) return PARENT_META[parentSlug];
  return { color: '#1B4F72', accent: '#0F2F45' };
}


/** Convenience component. */
export function CategoryIcon({ category, slug, style, className, fallback }) {
  const target = category || slug;
  const Icon = getCategoryIcon(target, fallback);
  return <Icon style={style} className={className} />;
}

export default getCategoryIcon;
